import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import type { TextMedia } from "@/types/kontent-generated/types/text-media.generated"
import { IKTImage, IKTRichText } from "@/types/kontent.types"
import { getDeliveryClient } from "@/lib/kontent/client"

import { assertItemResponse, fetchKontent } from "../helpers"
import {
  mapAssetToIKTImage,
  mapRichTextElementToIKTRichText,
  parseCustomElementJsonValue,
} from "../mappers"
import { resolveRichText } from "../richText"

export type TResolvedTextMedia = {
  title: string
  image?: IKTImage
  imagePosition: string
  tag: string
  text: IKTRichText
  icon: string
  textBackground: string
  style: string
}

/**
 * Maps an SDK TextMedia content item to TResolvedTextMedia.
 * Exported so that getTextMediaGroup can reuse it for its children.
 */
export function _mapTextMediaItem(item: TextMedia): TResolvedTextMedia {
  return {
    title: item.elements.title.value,
    image: mapAssetToIKTImage(item.elements.image.value[0]),
    tag: item.elements.tag.value,
    imagePosition: item.elements.image_position.value[0]?.name ?? "",
    text: mapRichTextElementToIKTRichText(item.elements.text),
    icon: parseCustomElementJsonValue(item.elements.icon.value) ?? "",
    style: item.elements.style.value[0]?.name ?? "",
    textBackground: item.elements.text_background.value[0]?.name ?? "",
  }
}

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetTextMediaFromItem(
  rawItem: IContentItem,
  previewMode = false
): Promise<TResolvedTextMedia> {
  const resolved = _mapTextMediaItem(rawItem as TextMedia)
  resolved.text = await resolveRichText(resolved.text, previewMode)
  return resolved
}

async function _fetchTextMedia(
  codename: string,
  previewMode = false
): Promise<TResolvedTextMedia> {
  return fetchKontent("TextMedia", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<TextMedia>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(2)
      .toPromise()

    const item = assertItemResponse(response, "TextMedia")
    return GetTextMediaFromItem(item, previewMode)
  })
}

export const GetTextMedia = cache(_fetchTextMedia)
